import { Controller } from "stimulus";
import { EventCurrencyChanged } from "../../../events";
import { formatCurrency, getActiveCurrency } from "../../../util/currency";
import Cookies from "js-cookie";

const DARK_COLOR = "#252525";
const LIGHT_COLOR = "#ffffff";
var chartJsonData = null;

export default class extends Controller {
  static targets = ["chart", "fullscreen", "close"];

  connect() {
    // This method is required, because it will detect both
    // button click to enable fullscreen or when a user press "esc"
    // to escape fullscreen
    window.addEventListener("fullscreenchange", () => {
      const closeEl = this.closeTarget;
      this._drawHighChart(chartJsonData);
      if (document.fullscreenElement) {
        // fullscreen is activated
        closeEl.classList.remove("tw-hidden");
      } else {
        // fullscreen is cancelled
        closeEl.classList.add("tw-hidden");
      }
    });

    window.addEventListener(EventCurrencyChanged, () => {
      this._fetchDataFromApi().then((jsonData) => {
        chartJsonData = jsonData;
        this._drawHighChart(chartJsonData);
      });
    });

    this._initialLoad();
  }

  _initialLoad() {
    Promise.all([
      import("highcharts/highstock"),
      import("highcharts/modules/heatmap"),
      import("highcharts/modules/treemap"),
    ]).then((res) => {
      const Highcharts = res[0];
      const Heatmap = res[1].default;
      const Treemap = res[2].default;

      Heatmap(Highcharts);
      Treemap(Highcharts);
      this.Highcharts = Highcharts;

      this._fetchDataFromApi().then((jsonData) => {
        chartJsonData = jsonData;
        this._drawHighChart(chartJsonData);
      });
    });
  }

  _fetchDataFromApi() {
    return fetch(
      `https://api.coingecko.com/api/v3/global/tree_map_chart_data?vs_currency=${getActiveCurrency()}`
    ).then((response) => response.json());
  }

  _drawHighChart(jsonData) {
    const IS_DARK = "is_dark";
    const darkMode = Cookies.get(IS_DARK) === "true";
    const chartHeight = window.innerHeight * 0.98;
    const backgroundColor = darkMode ? DARK_COLOR : LIGHT_COLOR;
    this.Highcharts.chart({
      time: { useUTC: false },
      title: {
        text: "",
      },
      subtitle: {
        text: "",
      },
      credits: {
        enabled: false,
      },
      colorAxis: {
        dataClasses: [
          { from: 0, to: 5, color: "#8DBE84" },
          { from: 5, to: 15, color: "#5D8456" },
          { from: 15, color: "#325C32" },
          { from: -5, to: 0, color: "#DE7875" },
          { from: -15, to: -5, color: "#B94A45" },
          { to: -15, color: "#9D2E29" },
        ],
      },
      legend: {
        enabled: false,
      },
      chart: {
        backgroundColor: backgroundColor,
        renderTo: this.chartTarget,
        type: "treemap",
        height: `${chartHeight}px`,
      },
      tooltip: {
        useHTML: true,
        followPointer: true,
        formatter: function () {
          var changeHTML;
          if (this.point.colorValue > 0) {
            changeHTML = `<span style="color:#5D8456;font-weight:700;">${parseFloat(
              this.point.colorValue
            ).toFixed(2)}%</span>`;
          } else {
            changeHTML = `<span style="color:#B94A45;font-weight:700;">${parseFloat(
              this.point.colorValue
            ).toFixed(2)}%</span>`;
          }

          return [
            "<div>",
            '<div style="font-weight:700;">',
            `<img style="width:20px; margin-right:5px;" src="${this.point.coinImageUrl}">`,
            `<span>${this.point.coinName}</span>`,
            "</div>",
            "<br>",
            `<div>Price: ${formatCurrency(this.point.price, getActiveCurrency(), false)}</div>`,
            `<div>24h Change: ${changeHTML}</div>`,
            `<div>Market Cap: ${formatCurrency(
              this.point.value,
              getActiveCurrency(),
              false
            )}</div>`,
            "</div>",
          ].join("");
        },
        style: {
          textAlign: "center",
          fontSize: "1rem",
        },
      },
      plotOptions: {
        treemap: {
          borderWidth: 2,
          layoutAlgorithm: "squarified",
          borderColor: backgroundColor,
          point: {
            allowPointSelect: true,
            events: {
              click: function (event) {
                location.href = `https://www.coingecko.com/resource_redirect?locale=en&route=coins/${event.point.coinId}`;
              },
            },
          },
        },
      },
      series: [
        {
          animation: false,
          dataLabels: {
            useHTML: true,
            style: {
              textAlign: "center",
            },
            formatter: function () {
              var changeHTML;
              if (this.point.colorValue > 0) {
                changeHTML = `<span style=font-weight:700;">+${parseFloat(
                  this.point.colorValue
                ).toFixed(2)}%</span>`;
              } else {
                changeHTML = `<span style=font-weight:700;">${parseFloat(
                  this.point.colorValue
                ).toFixed(2)}%</span>`;
              }
              var coinName = this.point.name.toUpperCase();
              var isWidthLarger = this.point.shapeArgs.width > this.point.shapeArgs.height;
              var fontSize = isWidthLarger
                ? this.point.shapeArgs.height / 8
                : this.point.shapeArgs.width / 8;
              var smallerFontSize = isWidthLarger
                ? this.point.shapeArgs.height / 12
                : this.point.shapeArgs.width / 12;

              return [
                `<a href="https://www.coingecko.com/resource_redirect?locale=en&route=coins/${this.point.coinId}" target="_blank" style="color:white;width:100%;height:100%;">`,
                `<div style="font-size:${fontSize}px;">${coinName}</div>`,
                `<div style="font-size:${smallerFontSize}px;font-weight:500;">${formatCurrency(
                  this.point.price,
                  getActiveCurrency()
                )}</div>`,
                `<div style="font-size:${smallerFontSize}px;font-weight:500;">${changeHTML}</div>`,
                "</a>",
              ].join("");
            },
          },
          data: jsonData,
        },
      ],
    });
  }

  openFullscreen() {
    const elem = this.fullscreenTarget;
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      /* Firefox */
      elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) {
      /* Chrome, Safari and Opera */
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      /* IE/Edge */
      elem.msRequestFullscreen();
    }
  }

  closeFullscreen() {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.mozCancelFullScreen) {
      /* Firefox */
      document.mozCancelFullScreen();
    } else if (document.webkitExitFullscreen) {
      /* Chrome, Safari and Opera */
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      /* IE/Edge */
      document.msExitFullscreen();
    }
  }
}
